import {X, Y} from './constants.js';

class KDTree {
    constructor(points) {
        if (points.length <= 1) {
            this.pivot = points[0];
            return;
        }

        let pivotAndDimension = this.getPivotAndDimension(points);

        this.pivot = pivotAndDimension.pivot;
        this.dimension = pivotAndDimension.dimension;
        this.range = pivotAndDimension.range;

        let index = points.indexOf(this.pivot);
        points.splice(index, 1);

        let subTrees = this.getSubTreePoints(
            this.pivot,
            this.dimension,
            points);

        this.leftSubTree = new KDTree(subTrees.left);
        this.rightSubTree = new KDTree(subTrees.right);
    }

    getSubTreePoints(pivot, dimension, points) {
        let leftPoints = [];
        let rightPoints = [];
        let pivotValue = this.getValueForDimension(pivot, dimension);

        for (let i in points) {
            let point = points[i];
            let value = this.getValueForDimension(point, dimension);
            if (value < pivotValue) {
                leftPoints.push(point);
            } else if (value >= pivotValue) {
                rightPoints.push(point);
            }
        }

        return {
            left: leftPoints,
            right: rightPoints,
        };
    }

    getValueForDimension(point, dimension) {
        return dimension === X ? point.point[0] : point.point[1];
    }

    getPivotAndDimension(points) {
        let dimensionObj = this.chooseDimension(points);
        let dimension = dimensionObj.dimension;

        // find midpoint in chosen dimension
        let midPoint = (dimensionObj.max + dimensionObj.min) / 2;

        // find pivot closest
        let closestPoint = undefined;
        let minDistance = 10000;

        for (let i in points) {
            let point = points[i];
            let distance = dimension === X ?
                Math.abs(point.point[0] - midPoint) :
                Math.abs(point.point[1] - midPoint);

            if (distance < minDistance) {
                closestPoint = point;
                minDistance = distance;
            }
        }

        return {
            pivot: closestPoint,
            dimension: dimension,
            range: {
                min:  dimensionObj.min,
                max: dimensionObj.max,
            }
        };
    }

    chooseDimension(points) {
        // length of x
        let minX = 10000;
        let minY = 10000;
        let maxX = -10000;
        let maxY = -10000;

        for (let i in points) {
            let x = points[i].point[0];
            let y = points[i].point[1];

            if (x < minX) {
                minX = x;
            }

            if (x > maxX) {
                maxX = x;
            }

            if (y < minY) {
                minY = y;
            }

            if (y > maxY) {
                maxY = y;
            }
        }

        let xLength = maxX - minX;
        let yLength = maxY - minY;

        if (xLength > yLength) {
            return {
                dimension: X,
                min: minX,
                max: maxX
            };
        } else {
            return {
                dimension: Y,
                min: minY,
                max: maxY,
            };
        }
    }

    getPointsInRange(x1, y1, x2, y2, depth=0) {
        if (this.pivot === undefined) {
            return [];
        }

        let x = this.pivot.point[0];
        let y = this.pivot.point[1];
        let points = [];
        if (x >= x1 && x <= x2 &&
            y >= y1 && y <= y2) {
            points.push(this.pivot);
        }


        // see if we should go left or right
        if (this.dimension == X) {
            if (x <= x2 && x1 <= this.range.max) {
                if (this.rightSubTree !== undefined) {
                    points = points.concat(this.rightSubTree.getPointsInRange(x1, y1, x2, y2, depth + 1));
                }
            }

            if (x >= x1 && x2 >= this.range.min) {
                if (this.leftSubTree !== undefined) {
                    points = points.concat(this.leftSubTree.getPointsInRange(x1, y1, x2, y2, depth + 1));
                }
            }
        } else if (this.dimension === Y) {
            if (y <= y2 && y1 <= this.range.max) {
                if (this.rightSubTree !== undefined) {
                    points = points.concat(this.rightSubTree.getPointsInRange(x1, y1, x2, y2, depth + 1));
                }
            }

            if (y >= y1 && y2 >= this.range.min) {
                if (this.leftSubTree !== undefined) {
                    points = points.concat(this.leftSubTree.getPointsInRange(x1, y1, x2, y2, depth + 1));
                }
            }
        }
        return points;
    }

    size() {
        let size = 1;

        if (this.leftSubTree !== undefined) {
            size += this.leftSubTree.size();
        }

        if (this.rightSubTree !== undefined) {
            size += this.rightSubTree.size();
        }

        return size;
    }

    inRange(x1, y1, x2, y2) {
        if (this.range === undefined) {
            return false;
        }

        if (this.dimension === X) {
            return x1 >= this.range.min && x1 <= this.range.max;
        } else {
            return y1 >= this.range.min && y1 <= this.range.max;
        }
    }
}

export default KDTree;
